import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { baConfig } from '../../config';
import { DataSourcesApi, DefaultApi, FeedRunApi } from '../../generated/data-core-svc';
// No need for a separate feature flag function as we'll accept the flag value as a parameter

// Extended API classes that give us access to the axios instance
class ExtendedDefaultApi extends DefaultApi {
  getAxiosInstance(): AxiosInstance {
    return this.axios;
  }
}

// Extended DataSourcesApi class that gives us access to the axios instance
class ExtendedDataSourcesApi extends DataSourcesApi {
  getAxiosInstance(): AxiosInstance {
    return this.axios;
  }
}

// Extended FeedRunApi class that gives us access to the axios instance
class ExtendedFeedRunApi extends FeedRunApi {
  getAxiosInstance(): AxiosInstance {
    return this.axios;
  }
}

class DataCoreApiManager {
  // disable caching of instance to mitigate delay of full feature flag load
  // private static defaultApiInstance: ExtendedDefaultApi;
  // private static dataSourcesApiInstance: ExtendedDataSourcesApi;
  // private static feedRunApiInstance: ExtendedFeedRunApi;

  /**
   * Normalize API URL to prevent double slashes
   * @param url The URL to normalize
   * @returns Normalized URL without trailing slash
   */
  private static normalizeApiUrl(url: string | undefined): string {
    if (!url) {
      console.warn('URL is undefined or empty');
      return '';
    }
    // Remove trailing slash to prevent double slashes when combined with endpoint paths
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  /**
   * Apply a request interceptor to fix trailing slashes in URLs
   * @param axiosInstance The Axios instance to apply the interceptor to
   * THIS IS A WORKAROUND FOR THE FACT THAT THE API INSTANCE IS CREATED WITH A TRAILING SLASH
   */
  private static applyTrailingSlashFix(axiosInstance: AxiosInstance): void {
    // Add an interceptor to the axios instance to fix trailing slashes

    axiosInstance.interceptors.request.use((config) => {
      // Fix the URL to ensure proper slash handling between data-sources and IDs
      if (config.url) {
        // Special handling for data-sources list endpoints which have trailing slashes in the generated code
        // Remove trailing slash from data-sources/ when it's at the end of the URL
        let processedUrl = config.url;

        // If the URL ends with data-sources/ (list endpoint), remove the trailing slash
        if (processedUrl.match(/\/data-sources\/$/) && !processedUrl.match(/\/data-sources\/[^/]/)) {
          processedUrl = processedUrl.replace(/\/data-sources\/$/, '/data-sources');
        }

        // Ensure there's a slash between data-sources and any ID
        const urlAfterFirstFix = processedUrl.replace(/(data-sources)([^/])/g, '$1/$2');

        // Then handle any potential double slashes in other parts of the URL
        // but preserve the one we just added
        const finalUrl = urlAfterFirstFix.replace(/([^:])\/{2,}/g, '$1/');

        if (config.url !== finalUrl) {
          config.url = finalUrl;
        }
      }

      return config;
    });

    // Add response interceptor to log responses
    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('Response error:', {
          message: error.message,
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        });
        return Promise.reject(error);
      }
    );
  }

  /**
   * Gets the DefaultApi instance
   * @param useDataCoreV2 - Whether the import-v3 feature flag is enabled
   * @returns DefaultApi instance
   */
  public static getInstance(useDataCoreV2: boolean): DefaultApi {
    const apiUrl = useDataCoreV2 ? baConfig.api3Url : baConfig.api2Url;

    const normalizedUrl = DataCoreApiManager.normalizeApiUrl(apiUrl);

    let defaultApiInstance: ExtendedDefaultApi;
    try {
      defaultApiInstance = new ExtendedDefaultApi(undefined, normalizedUrl);

      // Apply the trailing slash fix to the API instance
      DataCoreApiManager.applyTrailingSlashFix(defaultApiInstance.getAxiosInstance());
    } catch (error) {
      console.error('Error creating DefaultApi instance:', error);
      throw error;
    }
    return defaultApiInstance;
  }

  /**
   * Gets the DataSourcesApi instance
   * @param useDataCoreV2 - Whether the import-v3 feature flag is enabled
   * @returns DataSourcesApi instance
   */
  public static getDataSourcesApi(useDataCoreV2?: boolean): ExtendedDataSourcesApi {
    const apiUrl = useDataCoreV2 ? baConfig.api3Url : baConfig.api2Url;

    const normalizedUrl = DataCoreApiManager.normalizeApiUrl(apiUrl);

    let dataSourcesApiInstance: ExtendedDataSourcesApi;
    try {
      dataSourcesApiInstance = new ExtendedDataSourcesApi(undefined, normalizedUrl);

      // Apply the trailing slash fix to the DataSourcesApi instance
      // This ensures consistent URL handling regardless of feature flags
      DataCoreApiManager.applyTrailingSlashFix(dataSourcesApiInstance.getAxiosInstance());
    } catch (error) {
      console.error('Error creating DataSourcesApi instance:', error);
      throw error;
    }

    return dataSourcesApiInstance;
  }

  /**
   * Get the FeedRunApi instance
   * only supported by data core v2
   * @returns FeedRunApi instance
   */
  public static getFeedRunApi(): ExtendedFeedRunApi {
    const apiUrl = baConfig.api3Url;

    const normalizedUrl = DataCoreApiManager.normalizeApiUrl(apiUrl);

    let feedRunApiInstance: ExtendedFeedRunApi;
    try {
      feedRunApiInstance = new ExtendedFeedRunApi(undefined, normalizedUrl);

      // Apply the trailing slash fix to the FeedRunApi instance
      DataCoreApiManager.applyTrailingSlashFix(feedRunApiInstance.getAxiosInstance());
    } catch (error) {
      console.error('Error creating FeedRunApi instance:', error);
      throw error;
    }

    return feedRunApiInstance;
  }
}

export default DataCoreApiManager;
